import React from 'react';
import { ProductDetailsCard } from '../organisms/ProductDetailsCard';
import { EnrollmentCardContainerComp } from '../organisms/EnrollmentCardContainer/EnrollmentCardContainerComp';
import type { SlotContent } from '../organisms/DynamicTabbedLayout/DynamicTabbedLayout.types';

import {
  normalizeProductDetailsCardProps,
  normalizeEnrollmentCardProps,
  normalizeLayeredFeatureCardProps,
  normalizeMerchandisingCoBrandCardProps,
  normalizeRiottMediaCardProps,
} from './slotPropNormalizers';
import { SlotTrackingContext, isRecord } from './slotPropNormalizerUtils';
import { isDcaTargetPayloadFailed } from './dynamicContentUtils';

// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  MerchandisingLayeredFeature,
  MerchandisingCoBrandCard,
  RiottMediaCardNonEditable,
} from '@marriott/mi-merch-components';

const CARD_TYPE_MAP = {
  layeredfeaturecard: 'layeredfeaturecard',
  productdetailscard: 'productdetailscard',
  enrollmentcard: 'enrollmentcard',
  cobrandcard: 'cobrandcard',
  riottmediacard: 'riottmediacard',
} as const;

type CardTypeKey = (typeof CARD_TYPE_MAP)[keyof typeof CARD_TYPE_MAP];

function isCardTypeKey(s: string): s is keyof typeof CARD_TYPE_MAP {
  return s in CARD_TYPE_MAP;
}

function normalizeCardType(input: string): CardTypeKey | null {
  const normalized = input
    .trim()
    .toLowerCase()
    .replace(/model$/, '');
  return isCardTypeKey(normalized) ? CARD_TYPE_MAP[normalized] : null;
}

function getDcaCardType(metaData: Record<string, unknown> | undefined): CardTypeKey {
  if (!metaData || typeof metaData !== 'object') return CARD_TYPE_MAP.productdetailscard;
  if (metaData['isMango'] === true) return CARD_TYPE_MAP.riottmediacard;
  if ((metaData['type'] as string)?.toLowerCase() === 'cobrand') return CARD_TYPE_MAP.cobrandcard;
  if ((metaData['type'] as string)?.toLowerCase() === 'non-cobrand') return CARD_TYPE_MAP.productdetailscard;
  return CARD_TYPE_MAP.productdetailscard;
}

/** Is the payload a CF-shaped payload */
function isCfFragmentPayload(payload: unknown): boolean {
  if (payload == null || typeof payload !== 'object') return false;
  const cardType = (payload as Record<string, unknown>)['cardType'];
  return typeof cardType === 'string' && cardType.length > 0;
}

/**
 * Prefer live payload for card-type resolution. Only use `cfFallback`’s `cardType` when there is **no** live
 * Target object yet (`payload` null/undefined); otherwise UXL payloads without `cardType` would incorrectly
 * follow CF’s card type instead of `metaData`/`metadata` (Riott, CoBrand, etc.).
 */
function getPayloadForCfCardTypeResolution(slot: SlotContent, rawPayload: unknown): unknown {
  if (isCfFragmentPayload(rawPayload)) return rawPayload;
  if (rawPayload != null) return rawPayload;
  if (slot?.type === 'dca' && isRecord(slot?.cfFallback) && isCfFragmentPayload(slot.cfFallback)) {
    return slot.cfFallback;
  }
  return rawPayload;
}

/**
 * DCA slots with null/invalid Target payloads still resolve to the correct card so merch/PDC loading UI runs.
 * Merges mbox from dcaTarget; does not copy CF fallback content into the normalized props.
 */
function buildDcaPayloadForNormalizers(slot: SlotContent): unknown {
  if (slot.type !== 'dca') return slot.payload;
  const mbox = slot.dcaTarget?.mboxParameter;
  const p = slot.payload;

  if (p === null || p === undefined) {
    return {
      mboxName: mbox,
      mboxParameter: mbox,
      isTargetContentLoaded: false,
    };
  }
  if (!isRecord(p)) return p;
  if (isDcaTargetPayloadFailed(p)) {
    return {
      ...p,
      mboxName: (p['mboxName'] as string | undefined) ?? mbox,
      mboxParameter: (p['mboxParameter'] as string | undefined) ?? mbox,
    };
  }
  return p;
}

function resolveEffectiveSlot(slot: SlotContent): { effectiveSlot: SlotContent; resolvedCardType: CardTypeKey } {
  const size = (slot?.size as 'square' | 'tall' | 'banner') ?? 'square';
  const rawPayload = slot?.payload;
  const effectivePayload = slot?.type === 'dca' ? buildDcaPayloadForNormalizers(slot) : rawPayload;
  const effectiveSlot: SlotContent = effectivePayload === slot.payload ? slot : { ...slot, payload: effectivePayload };
  const payloadForCfResolution = getPayloadForCfCardTypeResolution(slot, rawPayload);
  const useCfCardTypeResolution =
    slot?.type === 'cf' || (slot?.type === 'dca' && isCfFragmentPayload(payloadForCfResolution));
  const resolvedCardType =
    size === 'banner'
      ? CARD_TYPE_MAP.layeredfeaturecard
      : useCfCardTypeResolution
      ? normalizeCardType(String((payloadForCfResolution as Record<string, unknown>)?.['cardType'] ?? '')) ||
        CARD_TYPE_MAP.productdetailscard
      : getDcaCardType(
          ((effectivePayload as Record<string, unknown>)?.['metaData'] ??
            (effectivePayload as Record<string, unknown>)?.['metadata']) as Record<string, unknown> | undefined
        );

  return { effectiveSlot, resolvedCardType };
}

function trackingValue(value: unknown): string {
  return typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim();
}

function isDcaEnabled(value: unknown): boolean {
  return value === true || value === 'true';
}

function buildNonCobrandDcaList3Entry(trackingProperties: Record<string, unknown> | undefined): string | null {
  if (!trackingProperties || !isDcaEnabled(trackingProperties['isDCAEnabled'])) return null;
  if (trackingProperties['isMango'] === true || trackingProperties['isMango'] === 'true') return null;
  if (trackingValue(trackingProperties['type']).toLowerCase() === 'cobrand') return null;
  if (trackingProperties['isCoBrand'] === true || trackingProperties['isCoBrand'] === 'true') return null;
  if (trackingValue(trackingProperties['atCCeVar48'])) return null;

  const position = trackingValue(trackingProperties['trackingContentPosition']);
  const description = trackingValue(trackingProperties['trackingDescription']);
  const offerType = trackingValue(trackingProperties['trackingOfferType']);
  const tag = trackingValue(trackingProperties['trackingTag']);
  if (!position || !description || !offerType || !tag) return null;

  return `${position},msg=${description},type=${offerType},tag=${tag}`;
}

export function getDcaNonCobrandImpressionEntry(slot: SlotContent, trackingCtx?: SlotTrackingContext): string | null {
  if (slot?.type !== 'dca') return null;
  const { effectiveSlot, resolvedCardType } = resolveEffectiveSlot(slot);
  const ctx = { slot: effectiveSlot, trackingCtx };

  if (resolvedCardType === CARD_TYPE_MAP.layeredfeaturecard) {
    const props = normalizeLayeredFeatureCardProps(ctx);
    return buildNonCobrandDcaList3Entry(props.trackingProperties as Record<string, unknown> | undefined);
  }

  if (resolvedCardType === CARD_TYPE_MAP.productdetailscard) {
    const props = normalizeProductDetailsCardProps(ctx);
    return buildNonCobrandDcaList3Entry(props.trackingProperties as Record<string, unknown> | undefined);
  }

  return null;
}

function getRiottMediaKey(props: Record<string, unknown>): string {
  const adData = props['adData'];
  const firstAd = Array.isArray(adData) ? (adData[0] as Record<string, unknown> | undefined) : undefined;
  const assets = isRecord(firstAd?.['assets']) ? firstAd?.['assets'] : undefined;

  return [
    props['mboxParameter'] || '',
    props['isTargetContentLoaded'] === false ? 'pending' : 'loaded',
    firstAd?.['tracking_guid'] || firstAd?.['impression_tracking_url'] || '',
    assets?.['primaryImage_desktop'] || assets?.['primaryImage_mobile'] || '',
  ].join('|');
}

function getRiottAdLoadedValue(props: Record<string, unknown>): string {
  const mboxParameter = props['mboxParameter'];
  return typeof mboxParameter === 'string' && mboxParameter ? `${mboxParameter}_adLoaded` : '';
}

function HomepageRiottMediaCard(props: Record<string, unknown>): React.ReactElement {
  const firedAdLoadedRef = React.useRef('');
  const riottKey = getRiottMediaKey(props);

  React.useEffect(() => {
    const adLoadedValue = getRiottAdLoadedValue(props);
    if (
      props['isTargetContentLoaded'] !== true ||
      !adLoadedValue ||
      firedAdLoadedRef.current === adLoadedValue ||
      typeof window === 'undefined' ||
      typeof (window as unknown as { impressionTrack?: unknown }).impressionTrack !== 'function'
    ) {
      return;
    }

    (window as unknown as { impressionTrack: (value: string) => void }).impressionTrack(`evar48=${adLoadedValue}`);
    firedAdLoadedRef.current = adLoadedValue;
  }, [props]);

  return <RiottMediaCardNonEditable key={riottKey} {...props} />;
}

export function resolveSlotComponent(slot: SlotContent, trackingCtx?: SlotTrackingContext): React.ReactElement | null {
  const { effectiveSlot, resolvedCardType } = resolveEffectiveSlot(slot);
  const ctx = { slot: effectiveSlot, trackingCtx };

  switch (resolvedCardType) {
    case CARD_TYPE_MAP.layeredfeaturecard:
      return <MerchandisingLayeredFeature {...normalizeLayeredFeatureCardProps(ctx)} />;
    case CARD_TYPE_MAP.enrollmentcard:
      return <EnrollmentCardContainerComp {...normalizeEnrollmentCardProps(ctx)} />;
    case CARD_TYPE_MAP.cobrandcard:
      return <MerchandisingCoBrandCard {...(normalizeMerchandisingCoBrandCardProps(ctx) as object)} />;
    case CARD_TYPE_MAP.riottmediacard: {
      const riottProps = normalizeRiottMediaCardProps(ctx);
      return <HomepageRiottMediaCard key={getRiottMediaKey(riottProps)} {...riottProps} />;
    }
    case CARD_TYPE_MAP.productdetailscard:
    default:
      return <ProductDetailsCard {...normalizeProductDetailsCardProps(ctx)} />;
  }
}
